import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { DateUtils, FareUtils, HookUtils } from '../../../utils';
import { getDeparturePrices, getReturnPrices, dayPricesLoaded, isCheapestPriceInMonth } from '../../../redux/flexibleDates/selectors';
import { getTripType, getSegments } from '../../../redux/currentSearch/selectors';
import { CalendarTypeProp, SearchSegment, TripType, TripTypeProp } from '../../../models';
import { MonthContext } from '../../DatePicker/MonthContext';
import { DayPrice } from './DayPrice';
import { useFaresOnLoadContext } from './FaresCalendarContext';

const LOAD_DELAY = 500;
const LOAD_INITIAL_BLANK = 1000;
const LOAD_INITIAL_BLUR = 1500;
const LOAD_TIMEOUT = 5000;
const isDateSelected = (date, segments) => !!segments.find((s) => DateUtils.compare(date, s.date) === 0);

export const FaresRenderInner = ({
    departurePrices,
    returnPrices,
    calendarType,
    date,
    entered,
    isDayDisabled,
    tripType,
    segments,
    dayPricesLoaded,
    isCheapest,
}) => {
    const requestTimeout = React.useRef(null);

    const isSubscribed = React.useRef(true);
    const { visible: monthVisible } = React.useContext(MonthContext);
    const previousEntered = HookUtils.usePrevious(entered);
    const previousSegments = HookUtils.usePrevious(segments);

    const [isBlank, setBlank] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(false);
    const [isFakeLoading, setFakeLoading] = React.useState(false);
    const [shouldWait, setShouldWait] = React.useState(!dayPricesLoaded);
    const faresOnLoadContext = useFaresOnLoadContext();

    React.useEffect(() => {
        return () => {
            isSubscribed.current = false;
            clearTimeout(requestTimeout.current);
        };
    }, []);

    React.useEffect(() => {
        const hadEntered = entered === previousEntered;

        if (hadEntered && !dayPricesLoaded) {
            setBlank(true);
            setTimeout(() => {
                isSubscribed.current && setInitialLoading(true);
                isSubscribed.current && setBlank(false);
                setTimeout(() => {
                    isSubscribed.current && setInitialLoading(false);
                }, LOAD_INITIAL_BLUR);
            }, LOAD_INITIAL_BLANK);
        }
    }, [entered, previousEntered, dayPricesLoaded]);

    React.useEffect(() => {
        if (!dayPricesLoaded) {
            requestTimeout.current = setTimeout(() => isSubscribed.current && setShouldWait(false), LOAD_TIMEOUT);
        } else {
            setShouldWait(false);
            clearTimeout(requestTimeout.current);
        }
    }, [dayPricesLoaded]);

    React.useEffect(() => {
        const segmentChange = previousSegments !== segments;
        const isRoundTrip = tripType === TripType.ROUND_TRIP;
        const shouldFakeAnimation = segmentChange && monthVisible && isRoundTrip && !isDateSelected(date, segments);
        if (shouldFakeAnimation) {
            setBlank(true);
            setFakeLoading(true);
            setTimeout(() => {
                isSubscribed.current && setBlank(false);
                setTimeout(() => isSubscribed.current && setFakeLoading(false), LOAD_DELAY);
            }, LOAD_DELAY);
        }
    }, [previousSegments, segments, tripType, monthVisible, date]);


    React.useEffect(() => {
        if (DateUtils.compare(date, faresOnLoadContext.lastDate) === 0) {
            if (!initialLoading && !isFakeLoading && !shouldWait && !isBlank && !isDayDisabled) {
                faresOnLoadContext.setLastFareLoaded(true);
            }
        }
    }, [initialLoading, isFakeLoading, shouldWait, isBlank, isDayDisabled]);

    const { id } = segments;
    const millisecondsDate = date.getTime();

    const value = useMemo(() => tripType === TripType.ROUND_TRIP ? FareUtils[calendarType].getPrice({
        cheapestPrice: departurePrices[DateUtils.toString(date)],
        totalPrice: returnPrices[DateUtils.toString(date)],
        selectedDeparturePrice: departurePrices[DateUtils.toString(segments[0].date)],
        date,
        segments }) : departurePrices[DateUtils.toString(date)], [calendarType, millisecondsDate, departurePrices, returnPrices, id, tripType]);

    return (
        <DayPrice
            cheapest={isCheapest}
            hidden={isBlank || isDayDisabled}
            loading={initialLoading || isFakeLoading || shouldWait}
            selected={isDateSelected(date, segments)}
            value={value}
        />
    );
};

FaresRenderInner.propTypes = {
    date: PropTypes.instanceOf(Date).isRequired,
    dayPricesLoaded: PropTypes.bool.isRequired,
    departurePrices: PropTypes.object.isRequired,
    calendarType: CalendarTypeProp.isRequired,
    entered: PropTypes.bool.isRequired,
    isCheapest: PropTypes.bool.isRequired,
    isDayDisabled: PropTypes.bool.isRequired,
    returnPrices: PropTypes.object.isRequired,
    tripType: TripTypeProp.isRequired,
    segments: PropTypes.arrayOf(SearchSegment).isRequired,
};

const mapStateToPropsSelector = createStructuredSelector({
    dayPricesLoaded,
    departurePrices: getDeparturePrices,
    isCheapest: isCheapestPriceInMonth,
    returnPrices: getReturnPrices,
    tripType: getTripType,
    segments: getSegments,
});

export const ConnectedFaresRenderInner = connect(mapStateToPropsSelector)(FaresRenderInner);
